import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import ProjectService from "../../services/ProjectService";
import ProjectStoreService from "../../services/ProjectStoreService";
import VendorServices from "../../services/VendorServices";
import StockInReportService from "../../services/stockInReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from '../../assets/images/deviops_logo.png';
import { getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function StockInSummaryReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";
  const loginData = StorageService.loginData();
  const employeeId = loginData?.employeeId || "";
  const roleId = loginData?.roleId || "";

  const [vendorId, setVendorId] = useState("");
  const [vendorStoreId, setVendorStoreId] = useState("");
  const [vendorStores, setVendorStores] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [projectCategoryId, setProjectCategoryId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [stores, setStores] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [projects, setProjects] = useState([]);

  const [itemCategoryId, setItemCategoryId] = useState("");
  const [itemId, setItemId] = useState("");
  const [itemCategories, setItemCategories] = useState([]);
  const [items, setItems] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("All");

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const canLoadSupplierLocations = !!vendorId;
  const canLoadProjectCategory = true;
  const canLoadProject = !!projectCategoryId;
  const canLoadStore = !!projectId;
  const canLoadItemCategories = !!vendorId || !!projectId;

  const clearSupplierDependentFilters = () => {
    setVendorStoreId("");
    setProjectCategoryId("");
    setProjectId("");
    setStoreId("");
    setItemCategoryId("");
    setItemId("");
    setVendorStores([]);
    setStores([]);
    setItemCategories([]);
    setItems([]);
  };

  const clearProjectDependentFilters = () => {
    setProjectId("");
    setStoreId("");
    setItemCategoryId("");
    setItemId("");
    setStores([]);
    setItems([]);
    if (vendorId) {
      loadItemCategories({ vendorId, vendorStoreId, projectId: "" });
    } else {
      setItemCategories([]);
    }
  };

  const filteredProjects = projectCategoryId
    ? projects.filter((p) => String(p.projectCategoryId || p.categoryId || p.projectCategoryID) === String(projectCategoryId))
    : [];

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const loadAllVendors = async () => {
    try {
      const vendorRes = await StockInReportService.getVendors({ companyId, projectId });
      setVendors(Array.isArray(vendorRes?.data) ? vendorRes.data : vendorRes || []);
    } catch (e) {
      console.error("Supplier load error", e);
      setVendors([]);
    }
  };

  const loadProjectCategories = async (vId = vendorId, vsId = vendorStoreId) => {
    if (vId) {
      try {
        const res = await StockInReportService.getProjectCategories({
          companyId,
          vendorId: vId,
          vendorStoreId: vsId,
          employeeId,
          roleId,
        });
        setProjectCategories(res?.data || []);
      } catch (err) {
        console.error("Project category load error", err);
        setProjectCategories([]);
      }
    } else {
      try {
        const res = await ProjectCategoryService.getActive();
        setProjectCategories(res?.data || res || []);
      } catch (err) {
        console.error("Project category load error", err);
        setProjectCategories([]);
      }
    }
  };

  const loadProjects = async (vId = vendorId, vsId = vendorStoreId) => {
    if (vId) {
      try {
        const res = await StockInReportService.getProjects({
          companyId,
          vendorId: vId,
          vendorStoreId: vsId,
          employeeId,
          roleId,
        });
        setProjects(res?.data || []);
      } catch (err) {
        console.error("Project load error", err);
        setProjects([]);
      }
    } else {
      try {
        const res = await ProjectService.getActiveProjectsByEmployee(companyId, employeeId);
        setProjects(res?.data || []);
      } catch (err) {
        console.error("Project load error", err);
        setProjects([]);
      }
    }
  };

  const loadDropdowns = async () => {
    try {
      await loadProjectCategories("", "");
      await loadProjects("", "");
      await loadAllVendors();
    } catch (error) {
      console.error("Dropdown load error", error);
    }
  };

  const loadVendorsByProject = async (pId) => {
    if (!pId) {
      loadAllVendors();
      return;
    }

    try {
      const res = await StockInReportService.getVendorsByProject(pId, { companyId });
      const list = res?.data || [];
      if (list.length > 0) {
        setVendors(list);
      } else {
        setVendors([]);
      }
    } catch (err) {
      console.error("Vendor load error", err);
      setVendors([]);
    }
  };

  const loadSupplierLocationsByVendor = async (vId, pId = projectId) => {
    if (!vId) {
      setVendorStores([]);
      setVendorStoreId("");
      return;
    }

    try {
      const res = await StockInReportService.getSupplierLocationsByVendor(vId, {
        projectId: pId,
        companyId,
      });
      setVendorStores(res?.data || []);
    } catch (err) {
      console.error("Supplier location error", err);
      setVendorStores([]);
    }
  };

  const loadStoresByProject = async (pId) => {
    if (!pId) {
      setStores([]);
      setStoreId("");
      return;
    }

    try {
      const res = await ProjectStoreService.getProjectStoreById(pId);
      setStores(res?.data || res || []);
    } catch (err) {
      console.error("Store load error", err);
      setStores([]);
    }
  };

  const loadItemCategories = async (overrideFilters = {}) => {
    const vId = overrideFilters.vendorId !== undefined ? overrideFilters.vendorId : vendorId;
    const vsId = overrideFilters.vendorStoreId !== undefined ? overrideFilters.vendorStoreId : vendorStoreId;
    const pId = overrideFilters.projectId !== undefined ? overrideFilters.projectId : projectId;

    if (!vId) {
      setItemCategories([]);
      setItemCategoryId("");
      setItems([]);
      setItemId("");
      return;
    }

    try {
      const res = await StockInReportService.getItemCategories({
        vendorId: vId,
        vendorStoreId: vsId,
        projectId: pId,
        companyId: companyId || undefined,
      });
      setItemCategories(res?.data || []);
    } catch (err) {
      console.error("Item category load error", err);
      setItemCategories([]);
    }
  };

  const loadItemsByCategory = async (catId, overrideFilters = {}) => {
    if (!catId) {
      setItems([]);
      setItemId("");
      return;
    }

    const vId = overrideFilters.vendorId !== undefined ? overrideFilters.vendorId : vendorId;
    const vsId = overrideFilters.vendorStoreId !== undefined ? overrideFilters.vendorStoreId : vendorStoreId;
    const pId = overrideFilters.projectId !== undefined ? overrideFilters.projectId : projectId;

    try {
      const res = await StockInReportService.getItemsByCategory(catId, {
        vendorId: vId,
        vendorStoreId: vsId,
        projectId: pId,
        companyId: companyId || undefined,
      });
      setItems(res?.data || []);
    } catch (err) {
      console.error("Item load error", err);
      setItems([]);
    }
  };

  const loadSummaryReport = async (pageNumber = page, shouldValidate = false) => {
    if (shouldValidate) {
      if (projectCategoryId || projectId) {
        if (!projectCategoryId) {
          showMessage("Project Category is required");
          return;
        }
        if (!projectId) {
          showMessage("Project Name is required");
          return;
        }
      }

      if (startDate && !endDate) {
        showMessage("From Date and To Date are required");
        return;
      }

      if (!startDate && endDate) {
        showMessage("From Date and To Date are required");
        return;
      }

      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        showMessage("From Date should be less than or equal to To Date");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await StockInReportService.getApprovedSummaryReport({
        companyId: companyId || undefined,
        employeeId,
        roleId,
        vendorId: vendorId || undefined,
        vendorStoreId: vendorStoreId || undefined,
        projectCategoryId: projectCategoryId || undefined,
        projectId: projectId || undefined,
        storeId: storeId || undefined,
        itemCategoryId: itemCategoryId || undefined,
        itemId: itemId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        status: status || "All",
        page: pageNumber,
        limit,
      });

      setReports(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);
    } catch (error) {
      console.error(error);
      setReports([]);
      showMessage("Error fetching approved stock in summary report");
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setVendorId("");
    setVendorStoreId("");
    setVendorStores([]);
    setProjectCategoryId("");
    setProjectId("");
    setStoreId("");
    setStores([]);
    setItemCategoryId("");
    setItemId("");
    setItemCategories([]);
    setItems([]);
    setStartDate("");
    setEndDate("");
    setStatus("All");
    setReports([]);
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    loadAllVendors();
    loadProjectCategories("", "");
    loadProjects("", "");
  };

  const getDisplayItem = (row) => row?.item || row?.itemName || row?.itemDescription || row?.itemCode || "-";

  const getSummaryData = (data) => {
    const groups = {};
    const toNumberLocal = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    data.forEach((row) => {
      const supplier = row.supplier || "-";
      const location = row.supplierLocation || "-";
      const itemCategory = row.itemCategory || "-";
      const item = getDisplayItem(row);
      const qty = toNumberLocal(row.totalQuantity);

      const key = `${supplier}_${location}_${itemCategory}_${item}`;
      if (!groups[key]) {
        groups[key] = {
          supplier,
          location,
          itemCategory,
          item,
          totalQty: 0,
        };
      }
      groups[key].totalQty += qty;
    });

    return Object.values(groups).sort((a, b) => {
      if (a.supplier !== b.supplier) return a.supplier.localeCompare(b.supplier);
      if (a.location !== b.location) return a.location.localeCompare(b.location);
      if (a.itemCategory !== b.itemCategory) return a.itemCategory.localeCompare(b.itemCategory);
      return a.item.localeCompare(b.item);
    });
  };

  const exportExcel = async () => {
    const summaryResponse = await StockInReportService.getApprovedSummaryReport({
      companyId: companyId || undefined,
      employeeId,
      roleId,
      vendorId: vendorId || undefined,
      vendorStoreId: vendorStoreId || undefined,
      projectCategoryId: projectCategoryId || undefined,
      projectId: projectId || undefined,
      storeId: storeId || undefined,
      itemCategoryId: itemCategoryId || undefined,
      itemId: itemId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      status: status || "All",
      page: 1,
      limit: 10000,
    });

    const allSummaryRows = summaryResponse?.data || [];
    if (!allSummaryRows.length) {
      showMessage("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();

    const buildStockInSummarySheet = () => {
      const ws = {};
      ws["!merges"] = [];
      const thin = { style: "thin" };
      const center = { horizontal: "center", vertical: "center", wrapText: true };
      const companyName = activeCompany?.companyName || "Company";
      const reportDateStr = `Report Date: ${utility.formatExportDateTime(new Date())}`;

      let row = 0;
      XLSX.utils.sheet_add_aoa(ws, [[companyName, "", "", "", "", reportDateStr, "", "", ""]], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 4 } });
      ws["!merges"].push({ s: { r: row, c: 5 }, e: { r: row, c: 8 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, color: { rgb: "000000" }, sz: 12 }, alignment: { horizontal: "left", vertical: "center" } };
      ws[XLSX.utils.encode_cell({ r: row, c: 5 })].s = { font: { bold: true, color: { rgb: "000000" }, sz: 10 }, alignment: { horizontal: "right", vertical: "center" } };
      row += 1;

      XLSX.utils.sheet_add_aoa(ws, [["Stock In Summary Table"]], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 8 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 16, underline: true }, alignment: { horizontal: "center", vertical: "center" } };
      row += 2;

      const filterLabels = [
        ["Supplier:", vendorId ? vendors.find((v) => String(v.id) === String(vendorId))?.vendorName || vendorId : "All", "", "Supplier Location:", vendorStoreId ? vendorStores.find((vs) => String(vs.id || vs.vendorStoreId) === String(vendorStoreId))?.vendorStoreName || vendorStoreId : "All", "", "Project Category:", projectCategoryId ? projectCategories.find((pc) => String(pc.id) === String(projectCategoryId))?.categoryName || projectCategoryId : "All"],
        ["Project:", projectId ? projects.find((p) => String(p.id) === String(projectId))?.projectName || projectId : "All", "", "Store:", storeId ? stores.find((s) => String(s.storeId || s.id) === String(storeId))?.storeName || storeId : "All", "", "Item Category:", itemCategoryId ? itemCategories.find((ic) => String(ic.id || ic.itemCategoryId) === String(itemCategoryId))?.itemCategoryName || itemCategoryId : "All"],
        ["Item Name:", itemId ? items.find((i) => String(i.id) === String(itemId))?.itemName || itemId : "All", "", "From Date:", startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", "", "To Date:", endDate ? utility.formatDateDDMMMYYYY(endDate) : "-"],
        ["Status:", status || "All", "", "", "", "", "", "", ""],
      ];
      XLSX.utils.sheet_add_aoa(ws, filterLabels, { origin: { r: row, c: 0 } });
      row += filterLabels.length + 1;

      const headers = ["Received Date", "Supplier", "Supplier Location", "Project", "Store", "Item Category", "Item Name", "UOM", "Total Qty"];
      const headerRowIndex = row;
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });
      headers.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        ws[cell].s = { font: { bold: true, sz: 11 }, alignment: center, border: { top: thin, bottom: thin, left: thin, right: thin } };
      });
      row += 1;

      allSummaryRows.forEach((r) => {
        const recDtSerial = utility.convertDateToExcelSerial(r.receivedDate);
        const rowData = [
          recDtSerial || (r.receivedDate ? utility.formatDateDDMMMYYYY(r.receivedDate) : "-"),
          r.supplier || "-",
          r.supplierLocation || "-",
          r.project || "-",
          r.store || "-",
          r.itemCategory || "-",
          getDisplayItem(r),
          r.uom || "-",
          Number(r.totalQuantity || 0),
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
        rowData.forEach((_, colIndex) => {
          const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
          ws[cell].s = {
            font: { sz: 10 },
            alignment: {
              horizontal: colIndex === 8 ? "right" : colIndex === 0 || colIndex === 7 ? "center" : "left",
              vertical: "center",
            },
            border: { top: thin, bottom: thin, left: thin, right: thin },
          };

          if (colIndex === 0 && recDtSerial) {
            ws[cell].t = "n";
            ws[cell].v = recDtSerial;
            ws[cell].z = "dd-mm-yyyy";
          }

          if (colIndex === 8) {
            ws[cell].t = "n";
            ws[cell].v = Number(r.totalQuantity || 0);
            ws[cell].z = "0.000";
          }
        });
        row += 1;
      });

      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: headerRowIndex, c: 0 },
          e: { r: row - 1, c: headers.length - 1 },
        }),
      };

      // Total row for Main Table
      const totalQuantity = allSummaryRows.reduce((sum, item) => sum + Number(item.totalQuantity || 0), 0);
      const totalsRow = ["Total", "", "", "", "", "", "", "", totalQuantity];
      XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 7 } });
      for (let c = 0; c <= 8; c++) {
        const cell = XLSX.utils.encode_cell({ r: row, c });
        ws[cell] = ws[cell] || { t: "s", v: "" };
        ws[cell].s = {
          font: { bold: true, sz: 10 },
          alignment: { horizontal: c === 8 ? "right" : "center", vertical: "center" },
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };
        if (c === 8) {
          ws[cell].t = "n";
          ws[cell].v = totalQuantity;
          ws[cell].z = "0.000";
        }
      }
      row += 1;

      // Write Summary Table below main table
      row += 2;
      XLSX.utils.sheet_add_aoa(ws, [["Summary Table"]], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 4 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
        font: { bold: true, sz: 14, underline: true },
        alignment: { horizontal: "center", vertical: "center" },
      };
      row += 2;

      const summaryHeaders = ["Supplier", "Supplier Location", "Item Category", "Item Name", "Total Qty"];
      XLSX.utils.sheet_add_aoa(ws, [summaryHeaders], { origin: { r: row, c: 0 } });
      summaryHeaders.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        ws[cell].s = {
          font: { bold: true, sz: 11 },
          alignment: center,
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };
      });
      row += 1;

      const summaryData = getSummaryData(allSummaryRows);
      summaryData.forEach((sRow) => {
        const sRowData = [
          sRow.supplier,
          sRow.location,
          sRow.itemCategory,
          sRow.item,
          Number(sRow.totalQty || 0),
        ];

        XLSX.utils.sheet_add_aoa(ws, [sRowData], { origin: { r: row, c: 0 } });
        sRowData.forEach((_, colIndex) => {
          const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
          ws[cell].s = {
            font: { sz: 10 },
            alignment: { horizontal: colIndex === 4 ? "right" : "left", vertical: "center" },
            border: { top: thin, bottom: thin, left: thin, right: thin },
          };

          if (colIndex === 4) {
            ws[cell].t = "n";
            ws[cell].v = Number(sRow.totalQty || 0);
            ws[cell].z = "0.000";
          }
        });
        row += 1;
      });

      // Total row for Summary Table
      const summaryTotalQty = summaryData.reduce((sum, item) => sum + Number(item.totalQty || 0), 0);
      const summaryTotalsRow = ["Total", "", "", "", summaryTotalQty];
      XLSX.utils.sheet_add_aoa(ws, [summaryTotalsRow], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 3 } });
      for (let c = 0; c <= 4; c++) {
        const cell = XLSX.utils.encode_cell({ r: row, c });
        ws[cell] = ws[cell] || { t: "s", v: "" };
        ws[cell].s = {
          font: { bold: true, sz: 10 },
          alignment: { horizontal: c === 4 ? "right" : "center", vertical: "center" },
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };
        if (c === 4) {
          ws[cell].t = "n";
          ws[cell].v = summaryTotalQty;
          ws[cell].z = "0.000";
        }
      }

      ws["!cols"] = [
        { wch: 15 },
        { wch: 22 },
        { wch: 18 },
        { wch: 20 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 10 },
        { wch: 15 },
      ];
      return ws;
    };

    const buildSummarySheet = () => {
      const ws = {};
      ws["!merges"] = [];
      const thin = { style: "thin" };
      const center = { horizontal: "center", vertical: "center", wrapText: true };
      let row = 0;

      XLSX.utils.sheet_add_aoa(ws, [["Summary Table"]], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 4 } });
      ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, sz: 16, underline: true }, alignment: { horizontal: "center", vertical: "center" } };
      row += 2;

      const headers = ["Supplier", "Supplier Location", "Item Category", "Item Name", "Total Qty"];
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });
      headers.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        ws[cell].s = { font: { bold: true, sz: 11 }, alignment: center, border: { top: thin, bottom: thin, left: thin, right: thin } };
      });
      row += 1;

      const summaryData = getSummaryData(allSummaryRows);
      summaryData.forEach((sRow) => {
        const rowData = [
          sRow.supplier,
          sRow.location,
          sRow.itemCategory,
          sRow.item,
          Number(sRow.totalQty || 0),
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
        rowData.forEach((_, colIndex) => {
          const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
          ws[cell].s = {
            font: { sz: 10 },
            alignment: { horizontal: colIndex === 4 ? "right" : "left", vertical: "center" },
            border: { top: thin, bottom: thin, left: thin, right: thin },
          };

          if (colIndex === 4) {
            ws[cell].t = "n";
            ws[cell].v = Number(sRow.totalQty || 0);
            ws[cell].z = "0.000";
          }
        });
        row += 1;
      });

      // Total row for Summary Table
      const summaryTotalQty = summaryData.reduce((sum, item) => sum + Number(item.totalQty || 0), 0);
      const summaryTotalsRow = ["Total", "", "", "", summaryTotalQty];
      XLSX.utils.sheet_add_aoa(ws, [summaryTotalsRow], { origin: { r: row, c: 0 } });
      ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 3 } });
      for (let c = 0; c <= 4; c++) {
        const cell = XLSX.utils.encode_cell({ r: row, c });
        ws[cell] = ws[cell] || { t: "s", v: "" };
        ws[cell].s = {
          font: { bold: true, sz: 10 },
          alignment: { horizontal: c === 4 ? "right" : "center", vertical: "center" },
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };
        if (c === 4) {
          ws[cell].t = "n";
          ws[cell].v = summaryTotalQty;
          ws[cell].z = "0.000";
        }
      }

      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: 2, c: 0 },
          e: { r: row - 1, c: headers.length - 1 },
        }),
      };

      ws["!cols"] = [
        { wch: 22 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 15 },
      ];
      return ws;
    };

    XLSX.utils.book_append_sheet(wb, buildStockInSummarySheet(), "StockInSummaryTable");
    XLSX.utils.book_append_sheet(wb, buildSummarySheet(), "SummaryTable");

    const fileName = `StockInSummaryReport_${utility.formatDateDMY(new Date()).replace(/\//g, "-")}`;
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  const exportPdf = async () => {
    const summaryResponse = await StockInReportService.getApprovedSummaryReport({
      companyId: companyId || undefined,
      employeeId,
      roleId,
      vendorId: vendorId || undefined,
      vendorStoreId: vendorStoreId || undefined,
      projectCategoryId: projectCategoryId || undefined,
      projectId: projectId || undefined,
      storeId: storeId || undefined,
      itemCategoryId: itemCategoryId || undefined,
      itemId: itemId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      status: status || "All",
      page: 1,
      limit: 10000,
    });

    const allSummaryRows = summaryResponse?.data || [];
    if (!allSummaryRows.length) {
      showMessage("No data to export");
      return;
    }

    const doc = new jsPDF("landscape");
    const pdfLogo = await getPdfLogoDataUrl(logo);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageMargin = 14;
    const companyName = activeCompany?.companyName || "Company";
    const reportTitle = "Stock In Summary Table";

    const drawHeader = (yPos, title = reportTitle) => {
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      const companyNameWidth = doc.getTextWidth(companyName);
      const logoWidth = 5;
      const logoHeight = 10;
      const logoX = 14 + 6 + (companyNameWidth - logoWidth) / 2;
      const logoY = yPos;

      if (pdfLogo) {
        doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
      }
      doc.text(companyName, 14 + 6, yPos + 15);

      const reportPrefix = "Report Date: ";
      const reportDateStr = utility.formatExportDateTime(new Date());

      doc.setFont(undefined, "bold");
      doc.setFontSize(10);
      const prefixWidth = doc.getTextWidth(reportPrefix);

      doc.setFont(undefined, "normal");
      doc.setFontSize(10);
      const dateWidth = doc.getTextWidth(reportDateStr);
      const totalWidth = prefixWidth + dateWidth;
      const right = pageWidth - 14;
      const startX = right - totalWidth;

      doc.setFont(undefined, "bold");
      doc.text(reportPrefix, startX, yPos + 15);

      doc.setFont(undefined, "normal");
      doc.text(reportDateStr, startX + prefixWidth, yPos + 15);

      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      const titleWidth = doc.getTextWidth(title);
      const centerX = pageWidth / 2;
      doc.text(title, centerX, yPos + 27, { align: "center" });

      doc.setLineWidth(0.3);
      doc.line(centerX - titleWidth / 2, yPos + 29, centerX + titleWidth / 2, yPos + 29);
    };

    let yPosition = 8;
    drawHeader(yPosition, reportTitle);

    const drawFooter = (pageNumber, totalPages) => {
      doc.setFont(undefined, "bold");
      doc.setFontSize(8);

      if (pageNumber === totalPages) {
        doc.text("Prepared By", pageMargin, pageHeight - 18);
        doc.text("Checked By", pageWidth / 2, pageHeight - 18, { align: "center" });
        doc.text("Approved By", pageWidth - pageMargin, pageHeight - 18, { align: "right" });
      }

      doc.setFont(undefined, "normal");
      doc.setFontSize(9);
      doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: "center" });
    };

    doc.setFontSize(9);
    let xLeft = 14;
    let xRight = 115;
    let yHeader = 44;

    const supplierLabel = vendorId ? vendors.find((v) => String(v.id) === String(vendorId))?.vendorName || vendorId : "All";
    const supplierLocationLabel = vendorStoreId ? vendorStores.find((vs) => String(vs.id || vs.vendorStoreId) === String(vendorStoreId))?.vendorStoreName || vendorStoreId : "All";
    const projectCategoryLabel = projectCategoryId ? projectCategories.find((pc) => String(pc.id) === String(projectCategoryId))?.categoryName || projectCategoryId : "All";
    const projectLabel = projectId ? projects.find((p) => String(p.id) === String(projectId))?.projectName || projectId : "All";
    const storeLabel = storeId ? stores.find((s) => String(s.storeId || s.id) === String(storeId))?.storeName || storeId : "All";
    const itemCategoryLabel = itemCategoryId ? itemCategories.find((ic) => String(ic.id || ic.itemCategoryId) === String(itemCategoryId))?.itemCategoryName || itemCategoryId : "All";
    const itemLabel = itemId ? items.find((i) => String(i.id) === String(itemId))?.itemName || itemId : "All";

    doc.setFont(undefined, "bold");
    doc.text("Supplier:", xLeft, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(String(supplierLabel), xLeft + 35, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("Supplier Location:", xRight, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(String(supplierLocationLabel), xRight + 35, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("Project Category:", xLeft, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(String(projectCategoryLabel), xLeft + 35, yHeader + 5);

    doc.setFont(undefined, "bold");
    doc.text("Project:", xRight, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(String(projectLabel), xRight + 35, yHeader + 5);

    doc.setFont(undefined, "bold");
    doc.text("Store:", xLeft, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(String(storeLabel), xLeft + 35, yHeader + 10);

    doc.setFont(undefined, "bold");
    doc.text("Item Category:", xRight, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(String(itemCategoryLabel), xRight + 35, yHeader + 10);

    doc.setFont(undefined, "bold");
    doc.text("Item Name:", xLeft, yHeader + 15);
    doc.setFont(undefined, "normal");
    doc.text(String(itemLabel), xLeft + 35, yHeader + 15);

    doc.setFont(undefined, "bold");
    doc.text("From Date:", xRight, yHeader + 15);
    doc.setFont(undefined, "normal");
    doc.text(startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", xRight + 35, yHeader + 15);

    doc.setFont(undefined, "bold");
    doc.text("Status:", xLeft, yHeader + 20);
    doc.setFont(undefined, "normal");
    doc.text(status || "All", xLeft + 35, yHeader + 20);

    doc.setFont(undefined, "bold");
    doc.text("To Date:", xRight, yHeader + 20);
    doc.setFont(undefined, "normal");
    doc.text(endDate ? utility.formatDateDDMMMYYYY(endDate) : "-", xRight + 35, yHeader + 20);

    yPosition += 20;

    autoTable(doc, {
      startY: 72,
      margin: {
        top: 40,
        left: 14,
        right: 14,
        bottom: 25,
      },
      head: [["Received Date", "Supplier", "Supplier Location", "Project", "Store", "Item Category", "Item Name", "UOM", "Total Qty"]],
      body: allSummaryRows.map((r) => [
        r.receivedDate ? utility.formatDateDDMMMYYYY(r.receivedDate) : "-",
        r.supplier || "-",
        r.supplierLocation || "-",
        r.project || "-",
        r.store || "-",
        r.itemCategory || "-",
        getDisplayItem(r),
        r.uom || "-",
        Number(r.totalQuantity || 0).toFixed(3),
      ]),
      theme: "grid",
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { halign: "center" },
        7: { halign: "center" },
        8: { halign: "right" },
      },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) {
          drawHeader(8, reportTitle);
        }
      },
    });

    doc.addPage();
    const summaryPageNum = doc.getNumberOfPages();
    drawHeader(8, "Summary Table");

    const summaryBody = getSummaryData(allSummaryRows).map((sRow) => [
      sRow.supplier,
      sRow.location,
      sRow.itemCategory,
      sRow.item,
      Number(sRow.totalQty || 0).toFixed(3),
    ]);

    autoTable(doc, {
      startY: 42,
      margin: { top: 40, left: 14, right: 14, bottom: 25 },
      head: [["Supplier", "Supplier Location", "Item Category", "Item Name", "Total Qty"]],
      body: summaryBody,
      theme: "grid",
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        4: { halign: "right" },
      },
      didDrawPage: (data) => {
        if (data.pageNumber === summaryPageNum) return;
        drawHeader(8, "Summary Table");
      },
    });

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i += 1) {
      doc.setPage(i);
      if (i > 1) {
        drawHeader(8, i < summaryPageNum ? reportTitle : "Summary Table");
      }
      drawFooter(i, totalPages);
    }

    const fileName = `StockInSummaryReport_${utility.formatDateDMY(new Date()).replace(/\//g, "-")}`;
    doc.save(`${fileName}.pdf`);
  };

  useEffect(() => {
    if (companyId) {
      loadDropdowns();
      const today = new Date();
      setEndDate(today.toISOString().split("T")[0]);
      loadSummaryReport(1, false);
    }
  }, [companyId]);

  useEffect(() => {
    if (projectId) {
      loadStoresByProject(projectId);
      if (!vendorId) {
        loadVendorsByProject(projectId);
      }
      loadItemCategories({ vendorId, vendorStoreId, projectId });
      if (vendorId) {
        loadSupplierLocationsByVendor(vendorId, projectId);
      }

      const selectedProject = projects.find((p) => String(p.id || p.projectId) === String(projectId));
      if (selectedProject?.projectStartDate) {
        const formattedDate = new Date(selectedProject.projectStartDate).toISOString().split("T")[0];
        setStartDate(formattedDate);
      }
    } else {
      setStores([]);
      setStoreId("");
      setItemCategoryId("");
      setItemId("");
      setItems([]);
      if (!vendorId) {
        loadAllVendors();
      }
      if (vendorId) {
        loadSupplierLocationsByVendor(vendorId, "");
        loadItemCategories({ vendorId, vendorStoreId, projectId: "" });
      } else {
        setItemCategories([]);
      }
    }
  }, [projectId]);

  return (
    <>
      <Breadcrumb pageTitle="Stock In Summary Report" secondTitle="Reports" />
      <MessageDialog message={message} type={msgType} setMessage={setMessage} setMsgType={setMsgType} />

      <div className="card p-4 bg-white shadow-sm">
        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-3 mb-3">
          <div className="col">
            <label className="form-label">Supplier Name</label>
            <select
              className="form-select"
              value={vendorId}
              onChange={(e) => {
                const selectedVendorId = e.target.value;
                setVendorId(selectedVendorId);
                clearSupplierDependentFilters();
                if (selectedVendorId) {
                  loadSupplierLocationsByVendor(selectedVendorId, projectId);
                  loadProjectCategories(selectedVendorId, "");
                  loadProjects(selectedVendorId, "");
                  loadItemCategories({ vendorId: selectedVendorId, vendorStoreId: "", projectId: "" });
                } else {
                  loadProjectCategories("", "");
                  loadProjects("", "");
                }
              }}
            >
              <option value="">All</option>
              {vendors.map((vendor) => (
                <option key={vendor.id || vendor.vendorId} value={vendor.id || vendor.vendorId}>
                  {vendor.vendorName || vendor.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col">
            <label className="form-label">Supplier Location</label>
            <select
              className="form-select"
              value={vendorStoreId}
              onChange={(e) => {
                const selectedStore = e.target.value;
                setVendorStoreId(selectedStore);
                setProjectCategoryId("");
                setProjectId("");
                setStoreId("");
                setItemCategoryId("");
                setItemId("");
                setStores([]);
                setItems([]);
                loadProjectCategories(vendorId, selectedStore);
                loadProjects(vendorId, selectedStore);
                if (vendorId) {
                  loadItemCategories({ vendorId, vendorStoreId: selectedStore, projectId: "" });
                } else {
                  setItemCategories([]);
                }
              }}
              disabled={!canLoadSupplierLocations}
            >
              <option value="">All</option>
              {vendorStores.map((vs) => (
                <option key={vs.id || vs.vendorStoreId} value={vs.id || vs.vendorStoreId}>
                  {vs.vendorStoreName || vs.locationName || vs.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col">
            <label className="form-label">Project Category</label>
            <select
              className="form-select"
              value={projectCategoryId}
              onChange={(e) => {
                const selectedCategoryId = e.target.value;
                setProjectCategoryId(selectedCategoryId);
                clearProjectDependentFilters();
              }}
              disabled={!canLoadProjectCategory}
            >
              <option value="">Select Project Category</option>
              {projectCategories.map((category) => (
                <option key={category.id || category.projectCategoryId} value={category.id || category.projectCategoryId}>
                  {category.categoryName || category.projectCategoryName || category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col">
            <label className="form-label">Project Name</label>
            <select
              className="form-select"
              value={projectId}
              onChange={(e) => {
                const selectedProjectId = e.target.value;
                setProjectId(selectedProjectId);
                if (!selectedProjectId) {
                  clearProjectDependentFilters();
                  return;
                }

                setStoreId("");
                setItemCategoryId("");
                setItemId("");
                setStores([]);
                setItemCategories([]);
                setItems([]);

                if (vendorId && !vendors.some((v) => String(v.id || v.vendorId) === String(vendorId))) {
                  setVendorId("");
                  setVendorStoreId("");
                }

                loadStoresByProject(selectedProjectId);
                loadItemCategories({ vendorId, vendorStoreId, projectId: selectedProjectId });
                const selectedProject = projects.find((p) => String(p.id || p.projectId) === String(selectedProjectId));
                if (selectedProject?.projectStartDate) {
                  setStartDate(new Date(selectedProject.projectStartDate).toISOString().split("T")[0]);
                }
              }}
              disabled={!canLoadProject}
            >
              <option value="">Select Project Name</option>
              {filteredProjects.length === 0 ? (
                <option value="">No Projects Available</option>
              ) : (
                filteredProjects.map((project) => (
                  <option key={project.id || project.projectId} value={project.id || project.projectId}>
                    {project.projectName}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="col">
            <label className="form-label">Store</label>
            <select
              className="form-select"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              disabled={!canLoadStore}
            >
              <option value="">All</option>
              {stores.map((store) => (
                <option key={store.storeId || store.id} value={store.storeId || store.id}>
                  {store.storeName}
                </option>
              ))}
            </select>
          </div>

          <div className="col">
            <label className="form-label">Item Category</label>
            <select
              className="form-select"
              value={itemCategoryId}
              onChange={(e) => {
                const selectedCatId = e.target.value;
                setItemCategoryId(selectedCatId);
                setItemId("");
                if (selectedCatId) {
                  loadItemsByCategory(selectedCatId, { vendorId, vendorStoreId, projectId });
                } else {
                  setItems([]);
                }
              }}
              disabled={!canLoadItemCategories}
            >
              <option value="">All</option>
              {itemCategories.map((category) => (
                <option key={category.id || category.itemCategoryId} value={category.id || category.itemCategoryId}>
                  {category.itemCategoryName || category.categoryName || category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col">
            <label className="form-label">Item Name</label>
            <select
              className="form-select"
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
              disabled={!itemCategoryId}
            >
              <option value="">All</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.itemName ? `${item.itemName}${item.itemCode ? ` (${item.itemCode})` : ""}` : item.itemCode || "-"}
                </option>
              ))}
            </select>
          </div>

          <div className="col">
            <label className="form-label">From Date</label>
            <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div className="col">
            <label className="form-label">To Date</label>
            <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          <div className="col">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
            </select>
          </div>
        </div>

        <div className="mb-3 d-flex justify-content-end align-items-center gap-2">
          <button className="btn btn-secondary" onClick={handleClearFilters}>
            <i className="ti ti-refresh me-1"></i>
            Clear
          </button>
          <button className="btn btn-primary" onClick={() => loadSummaryReport(1, true)} disabled={loading}>
            <i className="ti ti-search me-1"></i>
            {loading ? "Searching..." : "View Report"}
          </button>
        </div>

        <div className="mb-3 d-flex justify-content-end align-items-center gap-2">
          <button className="btn btn-success" onClick={exportPdf} disabled={loading || !reports.length}>
            <i className="ti ti-file-type-pdf me-1"></i>
            Export PDF
          </button>
          <button className="btn btn-success" onClick={exportExcel} disabled={loading || !reports.length}>
            <i className="ti ti-file-spreadsheet me-1"></i>
            Export Excel
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Received Date</th>
                <th>Supplier</th>
                <th>Supplier Location</th>
                <th>Project</th>
                <th>Store</th>
                <th>Item Category</th>
                <th>Item Name</th>
                <th>UOM</th>
                <th>Total Qty</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((row, index) => (
                  <tr key={`${row.supplier}-${row.supplierLocation}-${row.project}-${row.store}-${row.item}-${index}`}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{row.receivedDate ? utility.formatDateDDMMMYYYY(row.receivedDate) : "-"}</td>
                    <td>{row.supplier || "-"}</td>
                    <td>{row.supplierLocation || "-"}</td>
                    <td>{row.project || "-"}</td>
                    <td>{row.store || "-"}</td>
                    <td>{row.itemCategory || "-"}</td>
                    <td>{row.item || row.itemName || row.itemCode || "-"}</td>
                    <td>{row.uom || "-"}</td>
                    <td>{Number(row.totalQuantity || 0).toFixed(3)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === 1}
                  onClick={() => loadSummaryReport(page - 1, false)}
                >
                  Prev
                </button>

                {[...Array(totalPages)]
                  .map((_, i) => i + 1)
                  .filter((p) => p >= page - 2 && p <= page + 2)
                  .map((p) => (
                    <button
                      key={p}
                      className={`btn btn-sm ${page === p ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => loadSummaryReport(p, false)}
                    >
                      {p}
                    </button>
                  ))}

                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => loadSummaryReport(page + 1, false)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
